const MENU_ITEMS = {
  DASHBOARD: 'DASHBOARD',
  ALLOCATIONS: 'ALLOCATIONS',
  BOOKINGS: 'BOOKINGS',
  CAPACITY: 'CAPACITY',
  GARAGES: 'GARAGES',
  HEROES: 'HEROES',
  COVERAGE_AREA: 'COVERAGE_AREA',
  PAYMENTS: 'PAYMENTS',
  POSTCODES: 'POSTCODES',
  PRICELIST: 'PRICELIST',
  PRODUCT: 'PRODUCT',
  COVERAGE_ZONES: 'COVERAGE_ZONES',
  CMS: 'CMS',
  CAMPAIGNS: 'CAMPAIGNS',
};

const GROUPS = {
  HERO_MANAGERS: 'HERO_MANAGERS',
  CASE_MANAGERS: 'CASE_MANAGERS',
  ADMIN: 'ADMIN',
  GARAGE_MANAGERS: 'GARAGE_MANAGERS',
  CUSTOMER_SUPPORT: 'CUSTOMER_SUPPORT',
  ACCOUNTANTS: 'ACCOUNTANTS',
  DEVELOPERS: 'DEVELOPERS',
  MARKETING: 'MARKETING',
  UNKNOWN: 'UNKNOWN',
};

const MOTRIO_GROUPS = {
  MOTRIO_ADMIN: 'MOTRIO_ADMIN',
  MOTRIO_DEVELOPERS: 'MOTRIO_DEVELOPERS',
  MOTRIO_ES: 'MOTRIO-ES',
  MOTRIO_FR: 'MOTRIO-FR',
  MOTRIO_IT: 'MOTRIO-IT',
  MOTRIO_NL: 'MOTRIO-NL',
  MOTRIO_PL: 'MOTRIO-PL',
  MOTRIO_PT: 'MOTRIO-PT',
  MOTRIO_RO: 'MOTRIO-RO',
  MOTRIO_UK: 'MOTRIO-UK',
};

const RENAULT_GROUPS = {
  RENAULT_ADMIN: 'RENAULT_ADMIN',
  RENAULT_DEVELOPERS: 'RENAULT_DEVELOPERS',
  RENAULT_MA: 'RENAULT-MA',
};

// Cognito groups can arrive with either underscores (MOTRIO_FR) or hyphens
// (MOTRIO-FR) depending on the source, so the partner membership check compares
// a canonical hyphenated form on both sides. This intentionally differs from
// the MENU_PERMISSIONS lookup below, whose keys (e.g. GARAGE_MANAGERS) rely on
// underscores and must not be rewritten.
const canonicalizePartnerGroup = group => group.toUpperCase().replace(/_/g, '-');

const MOTRIO_GROUP_VALUES = new Set(Object.values(MOTRIO_GROUPS).map(canonicalizePartnerGroup));
const RENAULT_GROUP_VALUES = new Set(Object.values(RENAULT_GROUPS).map(canonicalizePartnerGroup));

const MENU_PERMISSIONS = {
  [GROUPS.HERO_MANAGERS]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.POSTCODES,
    MENU_ITEMS.COVERAGE_ZONES,
  ],

  [GROUPS.CASE_MANAGERS]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.POSTCODES,
    MENU_ITEMS.CAPACITY,
    MENU_ITEMS.COVERAGE_ZONES,
  ],

  [GROUPS.ADMIN]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.CAPACITY,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.PAYMENTS,
    MENU_ITEMS.POSTCODES,
    MENU_ITEMS.PRICELIST,
    MENU_ITEMS.PRODUCT,
    MENU_ITEMS.COVERAGE_ZONES,
    MENU_ITEMS.CMS,
    MENU_ITEMS.CAMPAIGNS,
  ],

  [GROUPS.DEVELOPERS]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.CAPACITY,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.PAYMENTS,
    MENU_ITEMS.POSTCODES,
    MENU_ITEMS.PRICELIST,
    MENU_ITEMS.PRODUCT,
    MENU_ITEMS.COVERAGE_ZONES,
    MENU_ITEMS.CMS,
    MENU_ITEMS.CAMPAIGNS,
  ],

  [GROUPS.GARAGE_MANAGERS]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.POSTCODES,
    MENU_ITEMS.COVERAGE_ZONES,
  ],

  [GROUPS.CUSTOMER_SUPPORT]: [
    MENU_ITEMS.DASHBOARD,
    MENU_ITEMS.ALLOCATIONS,
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.COVERAGE_AREA,
    MENU_ITEMS.CAPACITY,
    MENU_ITEMS.COVERAGE_ZONES,
  ],

  [GROUPS.ACCOUNTANTS]: [
    MENU_ITEMS.BOOKINGS,
    MENU_ITEMS.GARAGES,
    MENU_ITEMS.HEROES,
    MENU_ITEMS.PAYMENTS,
    MENU_ITEMS.PRICELIST,
    MENU_ITEMS.COVERAGE_ZONES,
    MENU_ITEMS.PRODUCT,
  ],

  [GROUPS.MARKETING]: [
    MENU_ITEMS.PRODUCT,
    MENU_ITEMS.CMS,
    MENU_ITEMS.CAMPAIGNS,
  ],

  [GROUPS.UNKNOWN]: [],
};

const isMotrioGroup = group => MOTRIO_GROUP_VALUES.has(canonicalizePartnerGroup(group));
const isRenaultGroup = group => RENAULT_GROUP_VALUES.has(canonicalizePartnerGroup(group));

export const getMenuItemsForGroups = (groups = []) => {
  const allMenuItems = new Set();
  const normalizedGroups = groups.map(group => group.toUpperCase());

  if (normalizedGroups.some(group => isMotrioGroup(group) || isRenaultGroup(group))) {
    return [MENU_ITEMS.GARAGES];
  }

  normalizedGroups.forEach((group) => {
    const menuItems = MENU_PERMISSIONS[group] || MENU_PERMISSIONS[GROUPS.UNKNOWN];

    menuItems.forEach(item => allMenuItems.add(item));
  });

  return Array.from(allMenuItems);
};

export { GROUPS, MOTRIO_GROUPS, RENAULT_GROUPS, MENU_ITEMS, MENU_PERMISSIONS };
